/**
 * Phase 1.7 — Crops module index, v2.
 *
 * Group-by: category (vegetable, fruit, grain, forage, herb, flower).
 * Crops have a system-vs-ranch scope (parallel to templates) — surfaced
 * as a badge.
 *
 * Layout: each crop is rendered as a horizontal tile with the image on
 * the LEFT of the title (PR description called this out as the desired
 * layout). Falls back to the brand mark via EntityImage if no image is
 * uploaded. Tiles are buttons that navigate to /crops/:id.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sprout, Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, FilterBar, KpiCard, SectionHeader, ViewToggle, type ViewMode } from '@/components/v2';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';
import { AddEntityDialog } from '@/components/v2/dialogs/AddEntityDialog';
import { EntityImage } from '@/components/EntityImage';

interface Crop {
  id: string;
  ranch_id: string | null;
  name: string;
  category: 'vegetable' | 'fruit' | 'grain' | 'forage' | 'herb' | 'flower';
  growing_season: 'cool' | 'warm' | 'year_round';
  is_system: number;
  description: string | null;
}

const CATEGORY_ORDER: Crop['category'][] = ['vegetable', 'fruit', 'grain', 'forage', 'herb', 'flower'];
const CATEGORY_LABEL: Record<Crop['category'], string> = {
  vegetable: 'Vegetables',
  fruit: 'Fruits',
  grain: 'Grains',
  forage: 'Forage',
  herb: 'Herbs',
  flower: 'Flowers',
};
const SEASON_LABEL: Record<Crop['growing_season'], string> = {
  cool: 'Cool season',
  warm: 'Warm season',
  year_round: 'Year-round',
};

const VIEW_KEY = 'crops';

export function CropsPageV2(): JSX.Element {
  const navigate = useNavigate();
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_KEY] ?? 'grid';
  const [crops, setCrops] = useState<Crop[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [catFilter, setCatFilter] = useState<'all' | Crop['category']>('all');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'ranch' | 'system'>('all');
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  usePageHeader(
    'Crops',
    <Button
      size="sm"
      onClick={() => setAddOpen(true)}
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Plus className="h-4 w-4" />
      New crop
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load(): Promise<void> {
      try {
        const data = await authFetch<{ items: Crop[] }>('/api/crops');
        if (!cancelled) setCrops(data.items ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load crops');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const filtered = useMemo<Crop[]>(() => {
    const q = search.trim().toLowerCase();
    return crops.filter((c) => {
      if (q && !`${c.name} ${c.description ?? ''}`.toLowerCase().includes(q)) return false;
      if (catFilter !== 'all' && c.category !== catFilter) return false;
      if (scopeFilter === 'ranch' && c.is_system === 1) return false;
      if (scopeFilter === 'system' && c.is_system !== 1) return false;
      return true;
    });
  }, [crops, search, catFilter, scopeFilter]);

  const grouped = useMemo<Record<Crop['category'], Crop[]>>(() => {
    const out = CATEGORY_ORDER.reduce<Record<Crop['category'], Crop[]>>(
      (acc, c) => ({ ...acc, [c]: [] }),
      {} as Record<Crop['category'], Crop[]>,
    );
    for (const c of filtered) out[c.category].push(c);
    return out;
  }, [filtered]);

  const totals = useMemo(() => {
    const ranch = crops.filter((c) => c.is_system !== 1).length;
    const system = crops.filter((c) => c.is_system === 1).length;
    return { ranch, system };
  }, [crops]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || catFilter !== 'all' || scopeFilter !== 'all';
  const clear = (): void => { setSearch(''); setCatFilter('all'); setScopeFilter('all'); };

  return (
    <div className="space-y-6">
      <AddEntityDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title="New Crop"
        endpoint="/api/crops"
        entityType="crop"
        fields={[
          { name: 'name', label: 'Name', required: true, placeholder: 'Tomato' },
          {
            name: 'category',
            label: 'Category',
            type: 'select',
            required: true,
            defaultValue: 'vegetable',
            options: CATEGORY_ORDER.map((c) => ({ value: c, label: CATEGORY_LABEL[c] })),
          },
          {
            name: 'growing_season',
            label: 'Growing season',
            type: 'select',
            required: true,
            defaultValue: 'warm',
            options: [
              { value: 'cool', label: SEASON_LABEL.cool },
              { value: 'warm', label: SEASON_LABEL.warm },
              { value: 'year_round', label: SEASON_LABEL.year_round },
            ],
          },
          { name: 'description', label: 'Description', type: 'textarea' },
        ]}
        transform={(v) => ({
          name: v.name,
          category: v.category,
          growing_season: v.growing_season,
          description: v.description || null,
        })}
        onAdded={() => setRefreshTick((t) => t + 1)}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label="Crops"
          icon={Sprout}
          primary={{ label: 'Ranch', value: totals.ranch }}
          secondary={{ label: 'System', value: totals.system }}
        />
        <KpiCard label="Filter" primary={{ label: 'Showing', value: filtered.length }} secondary={{ label: 'Total', value: crops.length }} />
        <KpiCard
          label="Categories"
          primary={{ label: 'Distinct', value: CATEGORY_ORDER.filter((c) => grouped[c].length > 0).length }}
        />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search crops' }}
        filters={[
          <FilterSelect
            key="cat"
            value={catFilter}
            onChange={(v) => setCatFilter(v as 'all' | Crop['category'])}
            options={[
              { value: 'all', label: 'All categories' },
              ...CATEGORY_ORDER.map((c) => ({ value: c, label: CATEGORY_LABEL[c] })),
            ]}
          />,
          <FilterSelect
            key="scope"
            value={scopeFilter}
            onChange={(v) => setScopeFilter(v as 'all' | 'ranch' | 'system')}
            options={[
              { value: 'all', label: 'All scopes' },
              { value: 'ranch', label: 'Ranch only' },
              { value: 'system', label: 'System only' },
            ]}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">{error}</div> : null}

      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Sprout}
          title={hasActiveFilters ? 'No matches' : 'No crops yet'}
          description={hasActiveFilters ? 'Try clearing filters.' : 'Add a crop variety so you can plan plantings.'}
          action={
            hasActiveFilters ? <Button onClick={clear} variant="outline">Clear filters</Button> : <Button onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" />New crop</Button>
          }
        />
      ) : (
        CATEGORY_ORDER.filter((c) => grouped[c].length > 0).map((cat) => (
          <SectionHeader key={cat} title={CATEGORY_LABEL[cat]} count={grouped[cat].length} collapsible>
            {view === 'grid' ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {grouped[cat].map((c) => (
                  <CropTile key={c.id} crop={c} onOpen={() => navigate(`/crops/${c.id}`)} />
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border bg-surface dark:bg-surface-dark dark:border-border">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-raised text-left dark:bg-surface-raised-dark">
                    <tr>
                      <Th>Name</Th>
                      <Th>Season</Th>
                      <Th>Scope</Th>
                      <Th>Description</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[cat].map((c) => (
                      <tr
                        key={c.id}
                        className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-raised dark:hover:bg-surface-raised-dark"
                        onClick={() => navigate(`/crops/${c.id}`)}
                      >
                        <Td className="font-medium">{c.name}</Td>
                        <Td className="text-muted-foreground">{SEASON_LABEL[c.growing_season]}</Td>
                        <Td>
                          {c.is_system === 1 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-clay/10 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
                              <Sparkles className="h-3 w-3" />
                              System
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full bg-moss/10 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-moss dark:bg-moss-dark/20 dark:text-moss-dark">
                              Ranch
                            </span>
                          )}
                        </Td>
                        <Td className="text-muted-foreground line-clamp-1 max-w-md">{c.description || '\u2014'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionHeader>
        ))
      )}
    </div>
  );
}

function CropTile({ crop, onOpen }: { crop: Crop; onOpen: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex items-stretch gap-0 overflow-hidden rounded-xl border border-border bg-surface text-left transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-burnt-orange dark:bg-surface-dark dark:border-border"
    >
      <div className="w-32 shrink-0">
        <EntityImage entityType="crop" entityId={crop.id} alt={crop.name} />
      </div>
      <div className="flex flex-1 flex-col justify-between gap-1 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate font-semibold text-foreground">{crop.name}</h3>
            <p className="text-xs text-muted-foreground">{SEASON_LABEL[crop.growing_season]}</p>
          </div>
          {crop.is_system === 1 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-clay/10 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
              <Sparkles className="h-3 w-3" />
              System
            </span>
          ) : null}
        </div>
        {crop.description ? (
          <p className="line-clamp-2 text-xs text-muted-foreground">{crop.description}</p>
        ) : null}
      </div>
    </button>
  );
}

function FilterSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <th className={`px-4 py-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground ${className ?? ''}`}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;
}
